import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { Password, PasswordDTO } from "../types/password";
/* eslint-disable import/export */
export abstract class ChangeSpocPasswordRPC implements
  LeoRPC<ChangeSpocPasswordRPC.Request, ChangeSpocPasswordRPC.Response, ChangeSpocPasswordRPC.Errors.Errors>
{
  abstract execute(
    request: ChangeSpocPasswordRPC.Request
  ): Promise<LeoRPCResult<ChangeSpocPasswordRPC.Response, ChangeSpocPasswordRPC.Errors.Errors>>;
}

export namespace ChangeSpocPasswordRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    spocId: string;
    password: PasswordDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isChangeSpocPasswordRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly spocId: LeoUUID;
    readonly password: Password;
  
  
    constructor(
    projectId: LeoUUID, 
    spocId: LeoUUID, 
    password: Password
    ) {
      this.projectId = projectId;
      this.spocId = spocId;
      this.password = password;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const spocId = getLeoUuid(dto, "spocId", InvalidRequestSpocIdError);
      const password = Password.fromDTO(getObject(dto, "password", InvalidRequestPasswordError));
      return new Request(
        projectId,
        spocId,
        password
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          spocId: this.spocId.toDTO(),
          password: this.password.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ChangeSpocPasswordRPC(projectId='${this.projectId}',spocId='${this.spocId}',password='${this.password}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `ChangeSpocPasswordRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSpocId = "INVALID_SPOC_ID",
    InvalidPassword = "INVALID_PASSWORD",
    UnableToChangePassword = "UNABLE_TO_CHANGE_PASSWORD"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ChangeSpocPasswordRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ChangeSpocPasswordRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `ChangeSpocPasswordRPC.INVALID_SPOC_ID()`;
    };
    
    
    export interface InvalidPasswordDTO {
      code: string;
    }
    
    export class InvalidPassword extends LeoRPCError<RPCError> {
      readonly isInvalidPassword: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPassword;
        super(code);
      }
    
    
      toDTO(): InvalidPasswordDTO {
        const dto: InvalidPasswordDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPassword.prototype.toString = function toString(): string {
      return `ChangeSpocPasswordRPC.INVALID_PASSWORD()`;
    };
    
    
    export interface UnableToChangePasswordDTO {
      code: string;
    }
    
    export class UnableToChangePassword extends LeoRPCError<RPCError> {
      readonly isUnableToChangePassword: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnableToChangePassword;
        super(code);
      }
    
    
      toDTO(): UnableToChangePasswordDTO {
        const dto: UnableToChangePasswordDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnableToChangePassword.prototype.toString = function toString(): string {
      return `ChangeSpocPasswordRPC.UNABLE_TO_CHANGE_PASSWORD()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | InvalidSpocId | InvalidPassword | UnableToChangePassword;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidSpocIdDTO | InvalidPasswordDTO | UnableToChangePasswordDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        case RPCError.InvalidPassword:
          return new InvalidPassword();
        case RPCError.UnableToChangePassword:
          return new UnableToChangePassword();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends ChangeSpocPasswordRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSpocIdError extends ChangeSpocPasswordRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocIdError";
  }
}

export class InvalidRequestPasswordError extends ChangeSpocPasswordRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPasswordError";
  }
}


export class InvalidResponseInvalidProjectIdError extends ChangeSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ChangeSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSpocIdError extends ChangeSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

export class InvalidResponseInvalidPasswordError extends ChangeSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPasswordError";
  }
}

export class InvalidResponseUnableToChangePasswordError extends ChangeSpocPasswordRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnableToChangePasswordError";
  }
}

